import { useTranslate } from '@source/utilities/hooks';
import { isGermany } from '@source/utilities/is-germany';
import { toUnicode } from 'punycode/';

export interface ValidationType {
    [key: string]: (value: any) => undefined | string | Text;
}

export interface CustomValidationRegex {
    required: boolean;
    validationRegex: string | null;
    validationErrorMessage: string | null;
    requiredErrorMessage: string | null;
    value: any;
    maxLength?: number | null;
    maxLengthErrorMessage?: string | null;
}

let validationTypes = {
    required: (value: any) => (value ? undefined : useTranslate('FieldIsRequired')),
    email: (value: any) =>
        value &&
        !/^[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])+$/i.test(
            toUnicode(value),
        )
            ? useTranslate('PleaseEnterValidEmailAddress')
            : undefined,
    phone: (value: any) =>
        value && !/^[\s\-+()0-9]*(?:[\s\-+()]*[0-9]){4}[\s\-+()0-9]*$/.test(value)
            ? useTranslate('PleaseEnterValidPhoneNumber')
            : undefined,
    number: (value: any) =>
        isNaN(Number(value)) ? useTranslate('EnterValidHouseNumber') : undefined,
    text: (value: any) =>
        value && !/^[^\d]+$/.test(value) ? useTranslate('PleaseEnterValidText') : undefined,
    fullDate: (value: any) =>
        value && !/^(0?[1-9]|[12][0-9]|3[01])[\/\-](0?[1-9]|1[012])[\/\-]\d{4}$/.test(value)
            ? useTranslate('PleaseEnterValidDate')
            : undefined,
    zipCode: (value: any) => {
        if (!value) return;

        const isValid = isGermany()
            ? /\b(?!01000)(?!99999)(0[1-9]\d{3}|[1-9]\d{4})\b$/.test(value)
            : /^[1-9][0-9]{3} ?[a-z]{2}$/i.test(value);

        return isValid ? undefined : useTranslate('EnterValidPostCode');
    },
};

const validateField = (value: any, validateFs: string) => {
    const errors: (string | Text)[] = [];
    const types = validateFs.indexOf(',') > -1 ? validateFs.split(',') : [validateFs];

    types.forEach(type => {
        const error = validationTypes[type as keyof typeof validationTypes](value);
        if (error) errors.push(error);
    });

    return errors;
};

const extendValidations = (validations: ValidationType) => {
    validationTypes = Object.assign({}, validationTypes, validations);
};

const validateCustomRegex = ({
    value,
    requiredErrorMessage,
    validationErrorMessage,
    required,
    validationRegex,
    maxLength,
    maxLengthErrorMessage,
}: CustomValidationRegex) => {
    const errors: string[] = [];

    if (required && !value) {
        errors.push(requiredErrorMessage || '');
    }

    if (maxLength && value.length > maxLength) {
        errors.push(maxLengthErrorMessage || '');
    }

    if (validationRegex) {
        const regex = new RegExp(validationRegex);
        if (!regex.test(value)) errors.push(validationErrorMessage || '');
    }

    return errors;
};

export { validationTypes, validateField, extendValidations, validateCustomRegex };
